// Token Refresh Service - 15-minute tokens with 13-minute refresh timer
// PERMANENT URLs - Set in stone on 2025-10-14
import { getApiBaseUrl as getPermanentApiBaseUrl } from '../config/permanent-urls';
import CognitoAuthService from './cognitoAuthService';
import { apiService } from './apiService';
import { secureOpenAIService } from './secureOpenaiService';

class TokenRefreshService {
  private refreshInterval: NodeJS.Timeout | null = null;
  private refreshIntervalMinutes: number = 13; // Refresh every 13 minutes
  private isRefreshing: boolean = false;
  private activeRefreshPromise: Promise<void> | null = null;

  constructor() {
    this.startTokenMonitoring();
  }

  /**
   * Get API base URL (same logic as apiService)
   * Note: Currently unused but kept for future use
   */
  // @ts-ignore - Method intentionally unused but kept for future use
  private getApiBaseUrl(): string {
    // Check if we have a Vite-injected API base URL
    if (typeof __API_BASE_URL__ !== 'undefined') {
      return __API_BASE_URL__;
    }

    // Fallback to environment variable
    const envUrl = (import.meta as ImportMeta & { env: { VITE_API_BASE_URL?: string } }).env.VITE_API_BASE_URL;

    if (envUrl) {
      if (!envUrl.endsWith('/api/v1')) {
        return `${envUrl}/api/v1`;
      }
      return envUrl;
    }

    // Use permanent URLs (set in stone)
    return getPermanentApiBaseUrl();
  }

  /**
   * Start monitoring with fixed interval refresh
   */
  private startTokenMonitoring(): void {
    // Check token expiration on startup and redirect if expired
    const token = localStorage.getItem('token');
    if (token && this.isTokenExpiredOrExpiringSoon(token)) {
      // Token is expired - try to refresh, but if it fails, redirect immediately
      this.refreshTokenIfAvailable().catch(() => {
        // Refresh failed - redirect to login
        this.clearAuth();
      });
    } else {
      // Token is valid - refresh immediately on startup if token exists
      this.refreshTokenIfAvailable();
    }

    // Then refresh every 13 minutes
    this.refreshInterval = setInterval(() => {
      this.refreshTokenIfAvailable();
    }, this.refreshIntervalMinutes * 60 * 1000);
  }

  /**
   * Check if token is expired or expiring soon
   */
  public isTokenExpiredOrExpiringSoon(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return true; // Invalid token format
      }
      
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) {
        return true; // No expiration claim
      }
      
      const expirationTime = payload.exp * 1000;
      const now = Date.now();
      const timeUntilExpiry = expirationTime - now;
      
      // Consider expired if expired or expiring within 2 minutes
      return timeUntilExpiry <= 2 * 60 * 1000;
    } catch (error) {
      return true; // If we can't parse, assume expired
    }
  }

  /**
   * Refresh token if available (with expiration checking)
   */
  private async refreshTokenIfAvailable(): Promise<void> {
    if (this.isRefreshing) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    // Check if token is expired or expiring soon
    if (this.isTokenExpiredOrExpiringSoon(token)) {
      // Token is expired - try to refresh immediately
      await this.refreshToken();
    } else {
      // Token is still valid - refresh proactively anyway (every 13 minutes)
      await this.refreshToken();
    }
  }

  /**
   * Refresh the token using Cognito (not backend endpoint)
   * CRITICAL: API Gateway requires id_token, not access_token
   */
  private async refreshToken(): Promise<void> {
    if (this.isRefreshing) {
      return this.activeRefreshPromise ?? Promise.resolve();
    }

    this.isRefreshing = true;
    this.activeRefreshPromise = this.performRefreshToken();

    try {
      await this.activeRefreshPromise;
    } finally {
      this.isRefreshing = false;
      this.activeRefreshPromise = null;
    }
  }

  private async performRefreshToken(): Promise<void> {

    try {
      // Use Cognito's refreshToken method directly (not backend endpoint)
      const authResult = await CognitoAuthService.refreshToken();
      const { accessToken, idToken, profile } = authResult;
      
      // API Gateway Cognito User Pool authorizer requires id_token, not access_token
      const token_for_api = idToken || accessToken;
      
      // CRITICAL: Update apiService with new id_token
      apiService.setToken(token_for_api);
      secureOpenAIService.setToken(token_for_api); // Use id_token for API Gateway (same as apiService)
      
      // Store token in localStorage
      localStorage.setItem('token', token_for_api);
      
      // Update any auth context or state management
      this.onTokenRefreshed({ 
        access_token: token_for_api, 
        accessToken, 
        idToken, 
        profile 
      });
    } catch (error) {
      // If Cognito refresh fails, token is likely expired - clear auth
      this.clearAuth();
      throw error;
    }
  }

  /**
   * Clear authentication data
   */
  private clearAuth(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userProfile');
    
    // CRITICAL: Always redirect to login when auth is cleared
    // This ensures user is redirected even if CORS errors occur
    // Use setTimeout to ensure redirect happens even if called during error handling
    setTimeout(() => {
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }, 0);
  }

  /**
   * Callback when token is refreshed
   */
  private onTokenRefreshed(data: any): void {
    // Profile is stored in AuthContext state, not localStorage (security: prevents XSS exposure)
    // AuthContext will update profile automatically when it detects the token refresh event
    
    // Dispatch custom event for other components to listen
    window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: data }));
  }

  /**
   * Stop monitoring (call when user logs out)
   */
  public stopMonitoring(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    // Token refresh service stopped - no console log needed
  }

  /**
   * Manually trigger token refresh
   */
  public async forceRefresh(): Promise<boolean> {
    const tokenBefore = localStorage.getItem('token');
    if (!tokenBefore) {
      return false;
    }

    try {
      await this.refreshToken();
      const tokenAfter = localStorage.getItem('token');
      // Refresh is successful only if a token still exists and is not expiring immediately.
      return !!tokenAfter && !this.isTokenExpiredOrExpiringSoon(tokenAfter);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if service is currently refreshing
   */
  public isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
